// src/services/export/toProcessedRecord.ts
// Adaptador ProcessedInfrastructure → ProcessedRecord (F5.04a)

import proj4 from 'proj4';
import type { ProcessedInfrastructure } from '@/types/processing';
import type {
  ProcessedRecord,
  ConfianzaLevel,
  TipoGeo,
  DataSource,
} from '@/types/export.types';
import { TipologiaPTEL } from '@/types/tipologia';
import {
  aptitudAlbergue, motivoAlbergueCompleto, ETIQUETA_ESTADO_ALBERGUE,
} from '@/lib/albergue/aptitudAlbergue';
import { capacidadAlbergue } from '@/lib/albergue/capacidadAlbergue';
import { InfrastructureClassifier } from '@/services/classification/InfrastructureClassifier';
import { enrichCatastroTooltip } from '@/services/catastro/catastroEnricher';
import { deriveProcedencia, avisoCentroide } from '@/utils/procedenciaVia';
import {
  clasificarBandaPosicional,
  RMSE_POR_FUENTE,
} from '@/services/validation/UmbralCalidadPosicional';

// ============================================================================
// CONFIGURACIÓN PROJ4
// ============================================================================

if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs');
}

// ============================================================================
// MAPEOS
// ============================================================================

/** Palabras clave → TipologiaPTEL (19 categorías macro) */
const TIPOLOGIA_KEYWORDS: Record<TipologiaPTEL, string[]> = {
  SANITARIO: ['sanit', 'salud', 'hospital', 'centro de salud', 'consultorio', 'farmacia', 'clínica', 'clinic', 'ambulat'],
  EDUCATIVO: ['educa', 'colegio', 'instituto', 'escuela', 'guardería', 'universidad', 'ceip', 'ies'],
  POLICIAL: ['polic', 'guardia civil', 'seguridad', 'cuartel', 'comisaría'],
  BOMBEROS: ['bombero', 'parque de bomberos', 'sepei', 'spei'],
  CULTURAL: ['cultur', 'biblioteca', 'museo', 'teatro', 'casa de cultura', 'auditorio'],
  RELIGIOSO: ['religi', 'iglesia', 'parroquia', 'ermita', 'convento', 'cementerio'],
  DEPORTIVO: ['deport', 'piscina', 'polideportivo', 'campo de fútbol', 'pabellón', 'gimnasio', 'estadio'],
  MUNICIPAL: ['administrat', 'ayuntamiento', 'juzgado', 'oficina municipal', 'correos', 'hacienda'],
  SOCIAL: ['social', 'servicios sociales', 'centro de día', 'residencia', 'centro de mayores', 'asociación', 'asociacion'],
  COMBUSTIBLE: ['gasolinera', 'estación de servicio', 'estacion de servicio', 'combustible', 'depósito de combustible', 'glp'],
  EMERGENCIAS: ['emergenc', 'protección civil', 'proteccion civil', 'cruz roja', 'incendio', '112', 'cecopal'],
  HIDRAULICO: ['hidráulic', 'hidraulic', 'depósito de agua', 'deposito de agua', 'edar', 'etap', 'embalse', 'pozo', 'sondeo', 'agua potable'],
  ENERGIA: ['energía', 'energia', 'subestación', 'subestacion', 'central eléctrica', 'central electrica', 'transformador', 'red eléctric', 'red electric', 'fotovoltaic', 'eólic', 'eolic'],
  TELECOM: ['telecom', 'telecomunicacion', 'antena', 'repetidor', 'torre de comunicaciones', 'centralita'],
  VIAL: ['vial', 'carretera', 'autovía', 'autovia', 'autopista', 'puente', 'túnel', 'tunel', 'rotonda'],
  INDUSTRIAL: ['industria', 'polígono industrial', 'poligono industrial', 'fábrica', 'fabrica', 'sustancias peligrosas', 'seveso'],
  RSU: ['residuos', 'punto limpio', 'vertedero', 'reciclaje', 'planta de tratamiento'],
  PUERTO: ['puerto', 'muelle', 'embarcadero', 'dársena', 'darsena'],
  GENERICO: [],
};

/** Tipos representativos (infraestructura lineal/red) */
const TIPOS_REPRESENTATIVOS = ['vial', 'hidráulic', 'hidraulic', 'energía', 'energia', 'telecom', 'telecomunicacion', 'carretera', 'red eléctric', 'red electric'];

/** Mapeo confidence → ConfianzaLevel */
const CONFIANZA_MAP: Record<string, ConfianzaLevel> = {
  CRITICA: 'critica',
  ALTA: 'alta',
  MEDIA: 'media',
  BAJA: 'baja',
  NULA: 'critica',
};

/** Fuentes válidas como DataSource */
const VALID_SOURCES: Set<string> = new Set(['DOC', 'IGN', 'DERA', 'CDAU', 'CATASTRO', 'CARTOCIUDAD']);

/**
 * Extrae DataSource[] desde el campo source de ProcessedInfrastructure.
 *
 * BUG-10 FIX: el hook useAutoGeocoding escribe source con prefijos compuestos:
 *   "geocoded:cartociudad"          → CARTOCIUDAD
 *   "corrected:cdau_r2"             → CDAU
 *   "confirmed:document"            → DOC
 *   "original:minor_discrepancy"    → DOC
 *   "geocoded:dera_viaria:EXACTO"   → DERA
 *   "singleton:sanitario"           → DERA  (datos locales)
 *   "geocoding_failed" / "none"     → []
 *
 * La lógica anterior esperaba tokens limpios ('CDAU', 'DOC'…) que nunca
 * llegan desde el pipeline moderno, dejando fuentes=[] en toda exportación.
 */
function parseFuentes(source: string): DataSource[] {
  if (!source || source === 'none' || source === 'geocoding_failed' || source === 'geocoding_error') {
    return [];
  }

  const lower = source.toLowerCase();
  const found = new Set<DataSource>();

  // PASO 1 — tokens directos (formato legacy 'DOC+DERA+CDAU' o 'CDAU, CARTOCIUDAD')
  // Siempre se ejecuta primero para mantener el orden de inserción del string original.
  source.split(/[,;|+\s]+/)
    .map(s => s.trim().toUpperCase())
    .filter(s => VALID_SOURCES.has(s))
    .forEach(s => found.add(s as DataSource));

  // PASO 2 — alias para valores compuestos del pipeline moderno
  // "geocoded:cartociudad", "corrected:cdau_r2", "confirmed:document", etc.
  const aliasMap: Array<[string, DataSource]> = [
    ['cartociudad',        'CARTOCIUDAD'],
    ['catastro',           'CATASTRO'],
    ['cdau',               'CDAU'],
    ['dera',               'DERA'],
    ['nominatim',          'IGN'],
    ['overpass',           'IGN'],
    ['confirmed:document', 'DOC'],
    ['original',           'DOC'],
    ['document',           'DOC'],
    ['singleton',          'DERA'],
    ['disambiguated',      'DERA'],
    // UINCEN 12-sep-2026: el alias ['centroide' → 'DOC'] se retira. Un centroide
    // municipal (geocoded:…:centroide, iner2:centroide-municipio, centroid) no es
    // una fuente que confirme el punto y menos aún el documento; sale con fuentes=[].
    // La columna «Centroide municipal» en la exportación queda en su OBS.
  ];

  for (const [alias, canonical] of aliasMap) {
    if (lower.includes(alias)) {
      found.add(canonical);
    }
  }

  return Array.from(found);
}
// ============================================================================
// FUNCIONES DE MAPEO
// ============================================================================

/**
 * TIPUNI (12-sep-2026): escalón del diccionario por `tipo` de P1 (`mapTypology`,
 * `adaptExtraction.ts`) que `TIPOLOGIA_KEYWORDS` no tenía, ya en vocabulario 19
 * y sobre texto sin diacríticos. Sin él, al unificar P1 sobre esta función se
 * perdían 32 elementos del corpus (medido con la vara `p0_tipuni_20260912`) («Centro de Enseñanza» ×22, «Santuario» ×2,
 * «Construcción», «Edif. público», «Área de destacada concurrencia»…). El cajón
 * `infraestructura` de P1 (`deposito`, `electrica`, `carretera`…) NO se porta:
 * en 19 no tiene destino único y cae por `classify(tipo + nombre)` sin pérdida
 * (medido: 0 perdidos en C2, P1 proyecta GENERICO → infraestructura igual).
 */
const TIPOLOGIA_EXTRA_P1: ReadonlyArray<readonly [TipologiaPTEL, RegExp]> = [
  ['EDUCATIVO', /ensenanza|docente|\bcpr\b|ciclo\s+formativo/],
  ['POLICIAL', /guardia\s+civil|comisaria/],
  ['EMERGENCIAS', /proteccion\s+civil/],
  ['MUNICIPAL', /consistorial|delegacion|oficina\s+municipal|casa\s+consist|edificio\s+consist/],
  ['RELIGIOSO', /santuario|templo|capilla|catedral|monasterio|basilica/],
  ['CULTURAL', /patrimonio|archivo|construccion|edif\.\s*publico|casa\s+de\s+la\s+cultura/],
  ['DEPORTIVO', /\barea\s+de\s+destacada|areas?\s+recreativas?|campo\s+de\s+futbol|pabellon|cancha|instalacion\s+deportiva/],
  // Del cajón `infraestructura` de P1, lo que en 19 sí tiene destino único. Sin
  // esto, «Centros y Redes de Producción y Distribución Energética» (×71 en el
  // corpus; `energetica` no casa con `energia`) caía a `classify(tipo + nombre)`
  // y el nombre («C.T. Secc. Hospital Comares», «C.T. Polideportivo») ganaba.
  ['ENERGIA', /energetic|produccion\s+y\s+distribucion|centros?\s+y\s+redes|\btransformacion\b|electric/],
  ['INDUSTRIAL', /productos\s+peligrosos/],
];

/** Valores de `tipo` que no informan (cabeceras copiadas, «OTRO», vacío) */
const TIPOS_SIN_INFORMACION = new Set(['', '-', 'otro', 'infraestructura', 'tipo',
  'tipologia', 'tipo de elemento', 'categoria', 'clase', 'naturaleza']);

const normalizar = (s: string): string =>
  s.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Instancia compartida — sin estado mutable (misma que P1 y que el orquestador).
const _classifier = new InfrastructureClassifier();

/**
 * El clasificador devuelve los 19 de `TipologiaPTEL` más `MAREMOTO`
 * (`types/infrastructure.ts`), que aquí no existe.
 */
function aTipologiaPTEL(t: string): TipologiaPTEL {
  if (t === 'MAREMOTO') return 'EMERGENCIAS';
  return (t in TIPOLOGIA_KEYWORDS ? t : 'GENERICO') as TipologiaPTEL;
}

/**
 * Clasifica un elemento del documento en `TipologiaPTEL` (19 categorías).
 *
 * TIPUNI (12-sep-2026, `DOP-TIPUNI-VOCABULARIO`=a): es LA función de tipología
 * para P1 (tabla, proyectada a los 9 tipos del DS en `adaptExtraction.ts`),
 * P2 (color de identidad) y P4 (exportación). Antes P1 tenía su propia cascada
 * en 9 y esta solo leía `tipo`: 1 691 de 2 368 elementos del corpus caían a
 * GENERICO. Con la cascada completa: 1 124 (567 resueltos, 0 perdidos; 5
 * cambian de tipo por `DOP-TIPUNI-PRECEDENCIA`=b).
 *
 * Cascada, en este orden:
 *   1. literal 19 (`SANITARIO`…); `MUNICIPAL` decide por el nombre primero
 *      (`DOP-TIPUNI-PRECEDENCIA`=b: «MUNICIPAL · Biblioteca Municipal» es CULTURAL)
 *   2. `TIPOLOGIA_KEYWORDS` por `tipo`
 *   3. `TIPOLOGIA_EXTRA_P1` por `tipo` sin diacríticos
 *   4. `monumento`: el nombre decide, `CULTURAL` por defecto (`TIPDIC`)
 *   5. `classify(tipo + nombre)` si `tipo` informa, `classify(nombre)` si no (`TIPCAD`)
 *   6. `GENERICO`
 *
 * @param tipo   - `infra.tipo` crudo del documento
 * @param nombre - `infra.nombre`; sin él solo actúan los escalones 1-3
 */
export function mapTipologia(tipo: string, nombre = ''): TipologiaPTEL {
  const lower = tipo.toLowerCase().trim();
  const tieneNombre = nombre.trim().length > 0;

  // 1. Coincidencia exacta en mayúsculas (ya es un TipologiaPTEL)
  const upper = tipo.toUpperCase().trim() as TipologiaPTEL;
  if (upper in TIPOLOGIA_KEYWORDS) {
    if (upper === 'MUNICIPAL' && tieneNombre) {
      const porNombre = aTipologiaPTEL(_classifier.classify(nombre).type);
      if (porNombre !== 'GENERICO') return porNombre;
    }
    return upper;
  }

  // 2. Búsqueda por palabras clave
  for (const [key, keywords] of Object.entries(TIPOLOGIA_KEYWORDS)) {
    if (keywords.length === 0) continue;
    if (keywords.some(kw => lower.includes(kw))) {
      return key as TipologiaPTEL;
    }
  }

  // 3. Diccionario heredado de P1, sin diacríticos
  const n = normalizar(tipo);
  for (const [key, re] of TIPOLOGIA_EXTRA_P1) {
    if (re.test(n)) return key;
  }

  // 4. `monumento`: decide el nombre solo; cultural por defecto
  if (/monumento/.test(n)) {
    if (tieneNombre) {
      const porNombre = aTipologiaPTEL(_classifier.classify(nombre).type);
      if (porNombre !== 'GENERICO') return porNombre;
    }
    return 'CULTURAL';
  }

  // 5. Clasificador con el nombre (y el tipo, si informa)
  if (tieneNombre) {
    const informa = !TIPOS_SIN_INFORMACION.has(n) && n.length >= 3;
    return aTipologiaPTEL(_classifier.classify(informa ? `${tipo.trim()} ${nombre}` : nombre).type);
  }

  return 'GENERICO';
}

/** Mapea confidence string a ConfianzaLevel */
export function mapConfianza(confidence: string): ConfianzaLevel {
  return CONFIANZA_MAP[confidence] ?? 'critica';
}

/** Clasifica tipoGeo según lógica F5.06 */
export function clasificarTipoGeo(infra: ProcessedInfrastructure): TipoGeo {
  if (!infra.xFinal || !infra.yFinal) return 'SIN_DATOS';

  const tipoLower = infra.tipo.toLowerCase();
  if (TIPOS_REPRESENTATIVOS.some(t => tipoLower.includes(t))) return 'REPRESENTATIVO';

  if (infra.score >= 50) return 'EXACTO';

  return 'NO_GEOCODIFICABLE';
}

/** Genera motivoNoGeo legible */
function generarMotivoNoGeo(tipoGeo: TipoGeo, infra: ProcessedInfrastructure): string | undefined {
  switch (tipoGeo) {
    case 'EXACTO':       return undefined;
    case 'REPRESENTATIVO': return `Infraestructura de red (${infra.tipo}) — coordenada representativa`;
    case 'SIN_DATOS':    return 'Sin coordenadas en el documento original ni geocodificación disponible';
    case 'NO_GEOCODIFICABLE': return `Score insuficiente (${infra.score}/100) — requiere revisión manual`;
  }
}

/** Reproyecta UTM30 → WGS84 */
function utmToWgs84(x: number, y: number): [number, number] {
  return proj4('EPSG:25830', 'EPSG:4326', [x, y]) as [number, number];
}

/** Genera URLs de verificación manual */
function generarUrls(lon: number, lat: number): ProcessedRecord['urls'] {
  const latFixed = lat.toFixed(6);
  const lonFixed = lon.toFixed(6);
  return {
    googleMaps: `https://www.google.com/maps?q=${latFixed},${lonFixed}`,
    openStreetMap: `https://www.openstreetmap.org/?mlat=${latFixed}&mlon=${lonFixed}#map=17/${latFixed}/${lonFixed}`,
    deraViewer: `https://www.juntadeandalucia.es/institutodeestadisticaycartografia/DERA/?lon=${lonFixed}&lat=${latFixed}&zoom=17`,
  };
}

/** Genera observaciones sintetizadas a partir del contexto de normalización/geocodificación */
function generarObservaciones(infra: ProcessedInfrastructure): string | undefined {
  const parts: string[] = [];

  // Correcciones de normalización
  if (infra.normalization?.yCorrectionApplied) {
    const tipoCorr = infra.normalization.yCorrectionType ?? 'desconocida';
    parts.push(`Corrección Y aplicada (${tipoCorr})`);
  }

  // Warnings de normalización
  const normWarnings = infra.normalization?.warnings ?? [];
  if (normWarnings.length > 0) {
    parts.push(...normWarnings.slice(0, 2)); // máx 2 warnings
  }

  // CSVGEO (8-oct-2026, DOP-1 a + a2): la lista «Flags: …» ya no viaja en
  // OBSERVACIONES. Describía al candidato de referencia y no a la fila, y
  // contradecía a GEOCODIFICADO, SCORE y CONFIANZA (Guadix, filas 2 y 195).
  // `scoreDetail.flags` sigue en el registro para los informes.

  // Errores de geocodificación
  const geoErrors = infra.geocoding?.errors ?? [];
  if (geoErrors.length > 0) {
    parts.push(`Geocoding: ${geoErrors[0]}`);
  }

  return parts.length > 0 ? parts.join(' | ') : undefined;
}

/** Resuelve el patrón de formato detectado como texto informativo.
 *  PatronDetectado describe el FORMAT (LIMPIO, COMA_DECIMAL, etc.), no el CRS.
 *  El CRS se determina a nivel de documento (ed50Detection) y se inyecta vía metadata. */
function resolverSistemaInicial(infra: ProcessedInfrastructure): string | undefined {
  if (infra.normalization) {
    const pattern = infra.normalization.pattern;
    if (pattern) return pattern;
  }
  return undefined;
}

/**
 * Estima el RMSE en metros a partir del geocoderUsed.
 * Usa la tabla RMSE_POR_FUENTE de UmbralCalidadPosicional como fuente de verdad.
 * Si geocoderUsed no coincide con ninguna clave, devuelve 999 (→ banda BAJA).
 */
function estimarRmsePorGeocoderUsed(geocoderUsed: string | undefined): number {
  if (!geocoderUsed) return 999;
  const key = geocoderUsed.toLowerCase();

  // Mapa de prefijos/alias → clave RMSE_POR_FUENTE
  if (key === 'doc' || key.startsWith('original')) return RMSE_POR_FUENTE['original-utm30']!;
  if (key.includes('cartociudad') && (key.includes('numero') || key.includes('number'))) return RMSE_POR_FUENTE['cartociudad-numero']!;
  if (key.includes('cartociudad')) return RMSE_POR_FUENTE['cartociudad-calle']!;
  if (key.includes('cdau') && (key.includes('numero') || key.includes('number')))  return RMSE_POR_FUENTE['cdau-numero']!;
  if (key.includes('cdau'))      return RMSE_POR_FUENTE['cdau-calle']!;
  if (key.includes('catastro'))  return RMSE_POR_FUENTE['catastro-rc']!;
  if (key.includes('dera'))      return RMSE_POR_FUENTE['dera-wfs-centroide']!;
  if (key.includes('nominatim') && key.includes('numero')) return RMSE_POR_FUENTE['nominatim-numero']!;
  if (key.includes('nominatim')) return RMSE_POR_FUENTE['nominatim-municipio']!;
  if (key.includes('centroide') || key.includes('ine')) return RMSE_POR_FUENTE['centroide-municipal']!;

  return 999;
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Convierte un array de ProcessedInfrastructure a ProcessedRecord[]
 * para consumo por los exportadores profesionales.
 *
 * @param infrastructures - Resultado del pipeline de normalización/geocodificación
 * @returns Array de registros listos para exportación
 */
function buildNotasValidacion(infra: ProcessedInfrastructure): string | undefined {
  const parts: string[] = [];
  const normWarnings = infra.normalization?.warnings ?? [];
  if (normWarnings.length > 0) parts.push(normWarnings.join(' | '));
  if (infra.geocoding?.geocodabilityCategory === 'SOCIAL_SIN_DATOS') {
    parts.push('GEO_NO_RESOLUBLE:sin_datos — infraestructura social sin dirección postal');
  }
  return parts.length > 0 ? parts.join(' | ') : undefined;
}

export function toProcessedRecords(
  infrastructures: ProcessedInfrastructure[],
): ProcessedRecord[] {
  return infrastructures.map((infra, index) => {
    const tipoGeo = clasificarTipoGeo(infra);
    const tipologia = mapTipologia(infra.tipo, infra.nombre);
    const confianza = mapConfianza(infra.confidence);
    const fuentes = parseFuentes(infra.source);
    const hasCoords = !!(infra.xFinal && infra.yFinal);
    // P4PROC: la vía que P1 ya deriva (`adaptExtraction`) viaja al entregable.
    const procedencia = deriveProcedencia(infra, hasCoords);
    const esCentroide = procedencia === 'centroide';
    // CENFOR-A (DOP-2=a): el aviso del centroide forzado viaja con el dato —
    // sustituye al «Score insuficiente» de MOTIVO y encabeza OBSERVACIONES.
    const aviso = esCentroide ? avisoCentroide(infra) : undefined;
    const observacionesBase = infra.observaciones ?? generarObservaciones(infra);
    const observaciones = [aviso, observacionesBase].filter(Boolean).join(' | ') || undefined;
    // F5.04c.2: scoreDetail directo o vía geocoding
    const sd = infra.scoreDetail ?? infra.geocoding?.scoreDetail;

    // Reproyección UTM30 → WGS84
    let lonWgs84 = 0;
    let latWgs84 = 0;
    let urls: ProcessedRecord['urls'] | undefined;

    if (hasCoords) {
      const [lon, lat] = utmToWgs84(infra.xFinal!, infra.yFinal!);
      lonWgs84 = lon;
      latWgs84 = lat;
      urls = generarUrls(lon, lat);
    }

    return {
      id: index + 1,
      nombre: infra.nombre,
      tipologia,
      tipoOriginal: infra.tipo,
      direccion: infra.direccion,
      // DIRCAJA-b: el extractor solo la rellena cuando la caja RAE cambió la dirección.
      direccionOriginal: infra.direccionOriginal,
      municipio: infra.municipio ?? '',
      provincia: infra.provincia ?? '',
      telefono: infra.telefono,
      email: infra.email,
      observaciones,
      xInicial: infra.xOriginal ? parseFloat(infra.xOriginal) || undefined : undefined,
      yInicial: infra.yOriginal ? parseFloat(infra.yOriginal) || undefined : undefined,
      sistemaInicial: resolverSistemaInicial(infra),
      xUtm30: infra.xFinal ?? 0,
      yUtm30: infra.yFinal ?? 0,
      lonWgs84,
      latWgs84,
      score: infra.score,
      confianza,
      fuentes,
      numFuentes: fuentes.length,
      // P4PROC (DOP-P4PROC-1c): el centroide municipal no cuenta como geocodificado.
      geocodificado: hasCoords && tipoGeo !== 'SIN_DATOS' && !esCentroide,
      procedencia,
      alertas: [],
      // EXP-D01-D: warnings de normalización como texto legible para el técnico municipal
      notasValidacion: buildNotasValidacion(infra),
      tipoGeo,
      // CENFOR-A: sobre centroide, MOTIVO dice qué pasó y no «Score insuficiente».
      motivoNoGeo: aviso ?? generarMotivoNoGeo(tipoGeo, infra),
      urls,
      // F5.04c.2: Scoring v2 detallado (fallback scoreDetail directo)
      scoreDetail: sd
        ? {
            d1: sd.d1.score,
            d2: sd.d2.score,
            d3: sd.d3.score,
            nivel: sd.nivel,
            flags: sd.flags,
          }
        : undefined,
      // F5.04c: Geocodificación detallada
      geocoderUsed: infra.geocoding?.geocoderUsed,
      geocodingAttempts: infra.geocoding?.attempts,
      geocodabilityCategory: infra.geocoding?.geocodabilityCategory,
      geocodabilityReason: infra.geocoding?.geocodabilityReason,
      // F7B.06: Aforo (campos preparados, se conectan al pipeline en F7B.05)
      aforoDeclarado: infra.aforoDeclarado,
      aforoUnidad: infra.aforoUnidad,
      aforoFuente: infra.aforoFuente,
      aforoConfianza: infra.aforoConfianza,
      refCatastral: infra.refCatastral,
      // ALBTIPO: misma tipología y mismo nombre que ve P2 (`adapters.ts`)
      ...(() => {
        const alb = aptitudAlbergue(tipologia, infra.nombre, infra.aforoDeclarado);
        return {
          albergueEstado: ETIQUETA_ESTADO_ALBERGUE[alb.estado],
          albergueMotivo: motivoAlbergueCompleto(alb),
          // ALBCAP-b: misma llamada que P2 (`Step2Page`): ficha + aforo declarado + aptitud.
          capacidadAlbergue: capacidadAlbergue(infra.fichaAlbergue, infra.aforoDeclarado, alb.estado),
        };
      })(),
      // ALBFICHA-b: la ficha de albergue del plan viaja tal cual; cada exportador la aplana.
      fichaAlbergue: infra.fichaAlbergue,
      // EXP-CAT-01: Datos Catastro R2
      // P4PROC (DOP-P4PROC-3a): sobre un centroide municipal la parcela que ata
      // el orquestador (por vía/nombre, `CTRN`) no describe el elemento, sino
      // una parcela cualquiera de esa calle: se omite. `infra.refCatastral`
      // (escrita en el documento) se conserva en la línea de arriba.
      ...(() => {
        if (!infra.catastroParcel || esCentroide) return {};
        const cat = enrichCatastroTooltip(infra.catastroParcel);
        return {
          catastroRefcat:        cat.rc,
          catastroSuperficie:   cat.superficie > 0 ? cat.superficie : undefined,
          catastroNplantas:     cat.plantas    > 0 ? cat.plantas    : undefined,
          catastroUso:          cat.uso,
          catastroAccesibilidad: cat.accesibilidad,
          catastroAnoConstr:     cat.anoConstr ?? undefined,
          // `AFORET` · DOP-2=(a) y DOP-3=(a): `catastroAforoEstimado` y
          // `catastroAforoMetodo` se dejan sin rellenar hasta `AFORCAT`. Las
          // columnas `CAT_AFORO_ESTIMADO` y `CAT_AFORO_METODO` siguen en el
          // CSV/XLSX (vacías) para no cambiar la estructura del fichero.
        };
      })(),
      // CAT-R2.01: Validación espacial buildings
      catastroEnEdificio: infra.catastroEnEdificio,
      catastroEdificioRc: infra.catastroEdificioRc,
      // UMBRAL-01: banda de calidad posicional (INVEST-01, 2026-03-27)
      bandaPosicional: clasificarBandaPosicional(
        estimarRmsePorGeocoderUsed(infra.geocoding?.geocoderUsed),
        infra.score
      ).banda,
    } satisfies ProcessedRecord;
  });
}
